import { Link } from '@tanstack/react-router';
import { Bookmark, Car, ChevronDown, Gavel, LayoutDashboard, LogOut, Receipt, UserRound } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { SessionUser } from '@/api';
import { buttonVariants } from '@/components/ui';
import { useBalance } from '@/hooks/use-balance';
import { useLogout } from '@/hooks/use-session';
import { cn } from '@/lib/utils';
import { formatUsdAmount } from '@/utils/format';

/** «Ahmed Al Mansouri» → «AM». Ініціали рахуємо з реальних полів, не з name. */
function initials(user: SessionUser): string {
	return `${user.firstName.at(0) ?? ''}${user.lastName.at(0) ?? ''}`.toUpperCase();
}

/**
 * Меню автентифікованого користувача.
 *
 * Settings із §2.1 спеки тут ще НЕМАЄ навмисно: екрана не існує, а пункт
 * меню, що веде не туди, гірший за відсутній.
 *
 * Закривається кліком поза межами і клавішею Escape — інакше на дотикових
 * екранах його неможливо прибрати, не перейшовши кудись.
 */
export function UserMenu({ user, showName = false }: { user: SessionUser; showName?: boolean }): ReactNode {
	const [open, setOpen] = useState(false);
	const container = useRef<HTMLDivElement>(null);
	const { logout, isPending } = useLogout();
	const { balance } = useBalance();

	useEffect(() => {
		if (!open) return;

		const onPointerDown = (event: PointerEvent): void => {
			if (!container.current?.contains(event.target as Node)) setOpen(false);
		};
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') setOpen(false);
		};

		document.addEventListener('pointerdown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);
		return (): void => {
			document.removeEventListener('pointerdown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [open]);

	return (
		<div ref={container} className="relative">
			{/* У вітрині — лише аватар: ім'я є в заголовку меню, а місце в шапці вже
			    займає баланс. Робоча зона просить ім'я поруч (`showName`). */}
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="flex items-center gap-1.5 rounded-full p-0.5 pr-1.5 transition-colors hover:bg-surface-muted"
				aria-expanded={open}
				aria-haspopup="menu"
				aria-label={`Account menu, ${user.firstName} ${user.lastName}`}
			>
				<span className="grid size-9 place-items-center rounded-full bg-accent text-[0.8125rem] font-semibold text-accent-fg">
					{initials(user)}
				</span>
				{/* У робочій зоні поруч стоїть ім'я: там місця вдосталь, а чип із
				    самими ініціалами читається як загадка. */}
				{showName && <span className="hidden text-sm font-medium lg:inline">{user.firstName}</span>}
				<ChevronDown className={cn('size-3.5 text-fg-muted transition-transform', open && 'rotate-180')} />
			</button>

			{open && (
				<div
					role="menu"
					/*
					  На мобільному кнопка стоїть біля лівого краю, тому прив'язка
					  до правого краю виносила меню за екран на 100+ пікселів.
					  Праворуч кріпимо лише там, де кнопка справді праворуч.
					*/
					className="absolute left-0 top-full z-50 mt-2 w-64 rounded-card border border-border bg-surface p-1.5 shadow-e3 lg:left-auto lg:right-0"
				>
					<div className="border-b border-border px-3 py-2.5">
						<p className="truncate text-sm font-medium">
							{user.firstName} {user.lastName}
						</p>
						<p className="truncate text-xs text-fg-muted">{user.email}</p>
						{user.companyName && (
							<p className="mt-1 truncate text-xs text-fg-subtle">{user.companyName}</p>
						)}
					</div>

					{balance && (
						<div className="flex items-baseline justify-between border-b border-border px-3 py-2 text-xs">
							<span className="text-fg-muted">Available deposit</span>
							<span className="font-mono font-medium tabular-nums">{formatUsdAmount(balance.availableUsd)}</span>
						</div>
					)}

					<Link
						to="/dashboard"
						role="menuitem"
						onClick={() => setOpen(false)}
						className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
					>
						<LayoutDashboard className="size-4" />
						Dashboard
					</Link>
					<Link
						to="/profile"
						role="menuitem"
						onClick={() => setOpen(false)}
						className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
					>
						<UserRound className="size-4" />
						Profile
					</Link>
					<Link
						to="/bids"
						role="menuitem"
						onClick={() => setOpen(false)}
						className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
					>
						<Gavel className="size-4" />
						My Bids
					</Link>
					<Link
						to="/vehicles"
						role="menuitem"
						onClick={() => setOpen(false)}
						className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
					>
						<Car className="size-4" />
						My Vehicles
					</Link>
					<Link
						to="/payments"
						role="menuitem"
						onClick={() => setOpen(false)}
						className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
					>
						<Receipt className="size-4" />
						Payments
					</Link>
					<Link
						to="/watchlist"
						role="menuitem"
						onClick={() => setOpen(false)}
						className="flex items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg"
					>
						<Bookmark className="size-4" />
						Watchlist
					</Link>

					<button
						type="button"
						role="menuitem"
						disabled={isPending}
						onClick={() => {
							setOpen(false);
							logout();
						}}
						className="flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-sm text-fg-muted hover:bg-surface-muted hover:text-fg disabled:opacity-60"
					>
						<LogOut className="size-4" />
						{isPending ? 'Logging out…' : 'Log out'}
					</button>
				</div>
			)}
		</div>
	);
}

/** Кнопки для гостя. Винесені сюди, щоб хедер не розгалужувався двічі. */
export function GuestActions({ onNavigate }: { onNavigate?: () => void }): ReactNode {
	return (
		<>
			<Link
				to="/login"
				onClick={onNavigate}
				className={buttonVariants({ variant: 'ghost', size: 'sm' })}
			>
				Log in
			</Link>
			<Link to="/register" onClick={onNavigate} className={buttonVariants({ size: 'sm' })}>
				Register
			</Link>
		</>
	);
}
